import type { Rng } from '../types';

const TWO_32 = 0x1_0000_0000;

/** Unbiased integer in [0, max) from a 32-bit uniform source using rejection sampling. */
function unbiased(draw: () => number, max: number): number {
  if (!Number.isInteger(max) || max <= 0) throw new Error(`nextInt: max must be a positive integer, got ${max}`);
  if (max === 1) return 0;
  const limit = Math.floor(TWO_32 / max) * max; // largest multiple of max that fits in 32 bits
  let v = draw();
  while (v >= limit) v = draw();
  return v % max;
}

/** Production RNG: crypto.getRandomValues + rejection sampling. Never Math.random for outcomes. */
export class CryptoRng implements Rng {
  private readonly buf = new Uint32Array(64);
  private idx = this.buf.length;

  private draw(): number {
    if (this.idx >= this.buf.length) {
      globalThis.crypto.getRandomValues(this.buf);
      this.idx = 0;
    }
    return this.buf[this.idx++];
  }

  nextInt(maxExclusive: number): number {
    return unbiased(() => this.draw(), maxExclusive);
  }
}

/** Deterministic RNG for tests, replays and simulations (mulberry32). */
export class SeededRng implements Rng {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0;
  }

  /** Raw 32-bit output. */
  private draw(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return (t ^ (t >>> 14)) >>> 0;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    return this.draw() / TWO_32;
  }

  nextInt(maxExclusive: number): number {
    return unbiased(() => this.draw(), maxExclusive);
  }
}
